import axios from 'axios';
import { logger } from '../../../../lib/logger';
import { RESPONSE_STATUS, PRECONDITION_CODE, CX_STATUS } from './constants';

const SIGNUP_URL = '/affiliates/signup';
const STATUS_URL = (rid) => `/affiliates_signup/api/v1/signup/${rid}`;

const POLL_DELAY = 1500;

export const ERROR_CODE = {
    FORBIDDEN: 'forbidden',
    EMAIL_TAKEN: 'emailTaken',
    NOT_FOUND: 'notFound',
    CLIENT_ERROR: 'clientError',
    SERVICE_ERROR: 'serviceError',
    CX_UNAVAILABLE: 'cxUnavailable',
    CX_UNKNOWN_ERROR: 'cxUnknownError',
    CX_EMAIL_TAKEN: 'cxEmailTaken',
    CX_USERNAME_TAKEN: 'cxUsernameTaken',
    CX_TELEGRAM_INVALID: 'cxTelegramInvalid',
};

const logAxiosError = (title) => (error) => {
    const { response, message } = error;
    const { status, data } = response;

    logger.error(title, {
        error: {
            message,
        },
        response: {
            status,
            body: data,
        },
    });

    throw error;
};

const poll = (rid) => {
    logger.info('Starting signup polling', { rid });

    const promise = new Promise((resolve, reject) => {
        const tick = () => {
            setTimeout(() => {
                axios
                    .get(STATUS_URL(rid))
                    .then((response) => {
                        const { status, data } = response;

                        logger.info('Signup polling tick', {
                            response: {
                                status,
                                body: data,
                            },
                        });

                        // Affiliate creation still ongoing
                        if (data.message === CX_STATUS.PROCESSING) {
                            return tick();
                        }

                        resolve(response);
                    }, logAxiosError(`Failed on ${STATUS_URL}`))
                    .catch(reject);
            }, POLL_DELAY);
        };

        tick();
    });

    return promise;
};

export const createAffiliate = (details) => {
    logger.info('Sending signup');

    return axios
        .post(SIGNUP_URL, details)
        .then(({ data }) => {
            const rid = data.message;

            return poll(rid);
        }, logAxiosError('Failed on /affiliates/signup'))
        .then((response) => {
            const { data } = response;
            const { success, message } = data;

            if (success === false) {
                const { status, data } = response;

                logger.error('Received signup failed status', {
                    response: {
                        status,
                        body: data,
                    },
                });

                throw {
                    message: 'CX signup failed',
                    response,
                };
            }

            const affiliateId = message;

            return affiliateId;
        })
        .catch((error) => {
            const { status } = error.response;

            if (status === RESPONSE_STATUS.FORBIDDEN) {
                throw ERROR_CODE.FORBIDDEN;
            }

            if (status === RESPONSE_STATUS.PRECONDITION_FAILED) {
                const { errors } = error.response.data;

                const emailOnly = errors.every((error) => error === PRECONDITION_CODE.EMAIL_TAKEN);

                if (emailOnly) {
                    throw ERROR_CODE.EMAIL_TAKEN;
                }

                // preconditin should be adjusted once updated
                // Keeping generic reason as email taken - the only validation for now.
                throw ERROR_CODE.EMAIL_TAKEN;
            }

            if (status === RESPONSE_STATUS.NOT_FOUND) {
                throw ERROR_CODE.NOT_FOUND;
            }

            if (status >= 400 && status < 500) {
                throw ERROR_CODE.CLIENT_ERROR;
            }

            if (status >= 500) {
                throw ERROR_CODE.SERVICE_ERROR;
            }

            if (status === RESPONSE_STATUS.CX_RESPONSE) {
                const { message } = error.response.data;

                if (message === CX_STATUS.UNAVAILABLE) {
                    throw ERROR_CODE.CX_UNAVAILABLE;
                }

                if (message === CX_STATUS.UNKNOWN_ERROR) {
                    throw ERROR_CODE.CX_UNKNOWN_ERROR;
                }

                if (message === CX_STATUS.EMAIL_TAKEN) {
                    throw ERROR_CODE.CX_EMAIL_TAKEN;
                }

                if (message === CX_STATUS.USERNAME_TAKEN) {
                    throw ERROR_CODE.CX_USERNAME_TAKEN;
                }

                if (message === CX_STATUS.TELEGRAM_INVALID) {
                    throw ERROR_CODE.CX_TELEGRAM_INVALID;
                }
            }

            throw ERROR_CODE.CLIENT_ERROR;
        });
};
